
import { useState } from "react";
import { MoreHorizontal, Shield, UserX, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";

interface AdminUser {
  id: string;
  email?: string;
  full_name?: string;
  avatar_url?: string;
  created_at?: string;
  roles?: string[];
  login_count?: number;
}

interface UserActionsMenuProps {
  user: AdminUser;
  onUserUpdated: () => void;
}

export const UserActionsMenu = ({ user, onUserUpdated }: UserActionsMenuProps) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Available roles
  const availableRoles = [
    'admin',
    'sales_assistant',
    'user',
    'atc',
    'dm'
  ];

  const handleAddRole = async (role: string) => {
    if (!user.email) {
      toast.error("Cannot update user without email");
      return;
    }
    
    try {
      setIsUpdating(true);
      
      const { data, error } = await supabase
        .rpc('add_user_role', {
          user_email: user.email,
          role_name: role
        });
      
      if (error) {
        throw error;
      }
      
      toast.success(`Role ${role} added to user`);
      onUserUpdated();
    } catch (error: any) {
      console.error("Error adding role:", error);
      toast.error(`Failed to add role: ${error.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemoveRole = async (role: string) => {
    if (!user.email) {
      toast.error("Cannot update user without email");
      return;
    }
    
    try {
      setIsUpdating(true);
      
      const { data, error } = await supabase
        .rpc('remove_user_role', {
          user_email: user.email,
          role_name: role
        });
      
      if (error) {
        throw error;
      }
      
      toast.success(`Role ${role} removed from user`);
      onUserUpdated();
    } catch (error: any) {
      console.error("Error removing role:", error);
      toast.error(`Failed to remove role: ${error.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  // Check if user has a specific role
  const hasRole = (role: string) => {
    return user.roles?.includes(role) || false;
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" disabled={isUpdating}>
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>User Actions</DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Shield className="mr-2 h-4 w-4" />
              <span>Manage Roles</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                {availableRoles.map(role => (
                  <DropdownMenuItem 
                    key={role}
                    disabled={isUpdating}
                    onClick={() => hasRole(role) 
                      ? handleRemoveRole(role) 
                      : handleAddRole(role)
                    }
                  >
                    {hasRole(role) ? (
                      <>
                        <Trash2 className="mr-2 h-4 w-4" />
                        <span>Remove {role}</span>
                      </>
                    ) : (
                      <>
                        <Shield className="mr-2 h-4 w-4" />
                        <span>Add {role}</span>
                      </>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
          
          <DropdownMenuSeparator />
          <DropdownMenuItem 
            className="text-destructive focus:text-destructive"
            onClick={() => setShowDeleteConfirm(true)}
          >
            <AlertTriangle className="mr-2 h-4 w-4" />
            <span>Delete User</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action is not yet implemented and would permanently delete the user 
              account for {user.full_name || user.email}.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                toast("User deletion is not implemented yet", {
                  description: "This feature will be available in a future update."
                });
                setShowDeleteConfirm(false);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
